import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { Plus, MapPin, Phone, Mail, KeyRound, ShieldAlert, Car, Trash2, Pencil, X, Info } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { Highlight } from "@/lib/highlight";
import { COLOR_PALETTE as COLORS } from "@/lib/colors";

const EMPTY = {
    name: "", contact_name: "", contact_phone: "", contact_email: "",
    address: "", gate_code: "", alarm_code: "",
    key_location: "", access_notes: "", parking_notes: "",
    color: "",
};

// Colour palette is imported from @/lib/colors

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null=closed, {} = new, {...client}=edit
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/clients");
            setClients(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (editing.id) {
                const { data } = await api.patch(`/clients/${editing.id}`, editing);
                setClients(prev => prev.map(c => c.id === data.id ? data : c));
                toast.success("Client updated");
            } else {
                const { data } = await api.post("/clients", editing);
                setClients(prev => [data, ...prev]);
                toast.success("Client added");
            }
            setEditing(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!confirm("Remove this client? Jobs already scheduled will keep their address text.")) return;
        try {
            await api.delete(`/clients/${id}`);
            setClients(prev => prev.filter(c => c.id !== id));
            toast.success("Client removed");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const filteredClients = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return clients;
        return clients.filter(c => {
            const hay = [
                c.name, c.contact_name, c.contact_phone, c.contact_email,
                c.address, c.gate_code, c.alarm_code, c.key_location,
                c.access_notes, c.parking_notes,
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [clients, query]);

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Client dossier</span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Client book</h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm">Access codes, alarm PINs and contact details — surfaced on every job.</p>
                </div>
                <button
                    data-testid="new-client-btn"
                    onClick={() => setEditing({ ...EMPTY })}
                    className="btn-primary inline-flex items-center gap-2"
                >
                    <Plus size={16} /> New client
                </button>
            </div>

            <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search clients — name, address, contact, gate code, alarm PIN…"
                resultLabel={query.trim() ? `${filteredClients.length} of ${clients.length} client${clients.length === 1 ? "" : "s"} match` : null}
            />

            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading clients…</div>
            ) : filteredClients.length === 0 ? (
                <div className="card-flat p-10 text-center" data-testid="clients-empty">
                    {query ? (
                        <>
                            <p className="font-display text-lg font-semibold">Nothing matched &ldquo;{query}&rdquo;</p>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">Try a shorter word, a street name, or a phone number.</p>
                            <button className="btn-ghost mt-4" onClick={() => setQuery("")}>Clear search</button>
                        </>
                    ) : (
                        <>
                            <p className="font-display text-lg font-semibold">No clients yet</p>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">Add your first client so operatives arrive prepared.</p>
                            <button className="btn-primary mt-4" onClick={() => setEditing({ ...EMPTY })}>Add client</button>
                        </>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
                    {filteredClients.map(c => (
                        <div key={c.id}
                             className="card-flat overflow-hidden cursor-pointer hover:border-[var(--pine)] transition-colors"
                             data-testid={`client-card-${c.id}`}
                             onClick={() => navigate(`/clients/${c.id}`)}
                        >
                            {c.color && <div className="h-2" style={{ background: c.color }} />}
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-display font-semibold text-lg truncate"><Highlight text={c.name} query={query} /></h3>
                                        {c.contact_name && (
                                            <div className="text-xs text-[var(--text-muted)] mt-0.5"><Highlight text={c.contact_name} query={query} /></div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                        <button data-testid={`edit-client-${c.id}`}
                                                onClick={(e) => { e.stopPropagation(); setEditing({ ...c }); }}
                                                className="btn-ghost !p-1.5" aria-label="Edit">
                                            <Pencil size={14} />
                                        </button>
                                        <button data-testid={`delete-client-${c.id}`}
                                                onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                                                className="btn-ghost !p-1.5 !text-red-600 !border-red-100 hover:!bg-red-50"
                                                aria-label="Delete">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1.5 text-sm text-[var(--text-secondary)]">
                                    {c.address && <Row icon={<MapPin size={13} />}><Highlight text={c.address} query={query} /></Row>}
                                    {c.contact_phone && <Row icon={<Phone size={13} />}><Highlight text={c.contact_phone} query={query} /></Row>}
                                    {c.contact_email && <Row icon={<Mail size={13} />}><Highlight text={c.contact_email} query={query} /></Row>}
                                    {c.gate_code && <Row icon={<KeyRound size={13} />}><b>Gate:</b> <code><Highlight text={c.gate_code} query={query} /></code></Row>}
                                    {c.alarm_code && <Row icon={<ShieldAlert size={13} />}><b>Alarm:</b> <code><Highlight text={c.alarm_code} query={query} /></code></Row>}
                                    {c.parking_notes && <Row icon={<Car size={13} />}><Highlight text={c.parking_notes} query={query} /></Row>}
                                    {c.access_notes && <Row icon={<Info size={13} />}><Highlight text={c.access_notes} query={query} /></Row>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <ClientDialog
                    value={editing}
                    onChange={setEditing}
                    onClose={() => setEditing(null)}
                    onSave={save}
                    busy={busy}
                    isNew={!editing.id}
                />
            )}
        </div>
    );
}

function Row({ icon, children }) {
    return (
        <div className="flex items-start gap-2">
            <span className="text-[var(--pine)] mt-0.5">{icon}</span>
            <span className="min-w-0 break-words">{children}</span>
        </div>
    );
}

function ClientDialog({ value, onChange, onClose, onSave, busy, isNew }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-in-up" onClick={onClose}>
            <div
                data-testid="client-dialog"
                className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-6 border-b border-[var(--border-soft)] sticky top-0 bg-white z-10">
                    <h3 className="font-display text-2xl font-semibold tracking-tight">
                        {isNew ? "New client" : "Edit client"}
                    </h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--bg-subtle)]" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={onSave} className="p-6 space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Client name</label>
                        <input data-testid="client-name-input" className="input-field mt-1" required
                               value={value.name} onChange={e => set("name", e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Contact name">
                            <input data-testid="client-contact-name-input" className="input-field mt-1"
                                   value={value.contact_name} onChange={e => set("contact_name", e.target.value)} />
                        </Field>
                        <Field label="Phone">
                            <input data-testid="client-phone-input" className="input-field mt-1"
                                   value={value.contact_phone} onChange={e => set("contact_phone", e.target.value)} />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Email">
                            <input data-testid="client-email-input" type="email" className="input-field mt-1"
                                   value={value.contact_email} onChange={e => set("contact_email", e.target.value)} />
                        </Field>
                        <Field label="Site address">
                            <input data-testid="client-address-input" className="input-field mt-1"
                                   value={value.address} onChange={e => set("address", e.target.value)} />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Gate code">
                            <input data-testid="client-gate-code-input" className="input-field mt-1"
                                   value={value.gate_code} onChange={e => set("gate_code", e.target.value)} />
                        </Field>
                        <Field label="Alarm code / PIN">
                            <input data-testid="client-alarm-code-input" className="input-field mt-1"
                                   value={value.alarm_code} onChange={e => set("alarm_code", e.target.value)} />
                        </Field>
                    </div>
                    <Field label="Key location">
                        <input data-testid="client-key-location-input" className="input-field mt-1"
                               value={value.key_location} onChange={e => set("key_location", e.target.value)}
                               placeholder="Lockbox, reception, keyholder…" />
                    </Field>
                    <Field label="Access notes">
                        <textarea data-testid="client-access-notes-input"
                                  className="input-field mt-1 min-h-[70px] resize-y"
                                  value={value.access_notes} onChange={e => set("access_notes", e.target.value)}
                                  placeholder="Anything the operative should know before arrival…" />
                    </Field>
                    <Field label="Parking">
                        <input data-testid="client-parking-input" className="input-field mt-1"
                               value={value.parking_notes} onChange={e => set("parking_notes", e.target.value)} />
                    </Field>
                    <Field label="Colour tag">
                        <p className="text-[11px] text-[var(--text-muted)] mt-1 mb-2">
                            Every job for this client shows this colour on the dashboard — pick something distinctive, or leave unassigned.
                        </p>
                        <div className="mt-1 grid grid-cols-8 gap-2 sm:grid-cols-10">
                            <button type="button"
                                    data-testid="client-color-none"
                                    onClick={() => set("color", "")}
                                    title="No colour"
                                    className={`h-9 w-9 rounded-full border-2 transition-transform flex items-center justify-center bg-white ${
                                        !value.color ? "border-black scale-110 shadow-md" : "border-[var(--border-soft)] hover:scale-105"
                                    }`}>
                                <X size={16} className="text-[var(--text-muted)]" />
                            </button>
                            {COLORS.map(c => (
                                <button key={c} type="button"
                                        data-testid={`client-color-${c}`}
                                        onClick={() => set("color", c)}
                                        title={c}
                                        className={`h-9 w-9 rounded-full border-2 transition-transform ${
                                            value.color === c ? "border-black scale-110 shadow-md" : "border-white hover:scale-105"
                                        }`}
                                        style={{ background: c }} />
                            ))}
                        </div>
                    </Field>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button data-testid="save-client-btn" type="submit" className="btn-primary" disabled={busy}>
                            {busy ? "Saving…" : isNew ? "Add client" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">{label}</label>
            {children}
        </div>
    );
}
